import { useCallback } from "react";

import { type AssignmentIntent, recordAssignments } from "@/models/assignment/assignmentWrites";
import type { AssignmentTarget } from "@/store/types";
import type { AcknowledgeGrain } from "./dispatchModel";
import { type DispatchTableRow, rowFeedId } from "./dispatchTableRow";
import { rowTarget } from "./useAcknowledgements";

/**
 * What a row hands off: the target its own acknowledgement would name (`rowTarget`), so a handoff and the
 * acknowledgement that closes it always name the same thing. A driver row hands off the driver's latest message, and
 * one with no message from the driver hands off nothing.
 */
function assignmentTargetOf(row: DispatchTableRow): Omit<AssignmentIntent, "assignee" | "note"> | null {
  const feedId = rowFeedId(row);
  if (!feedId) return null;
  const intent = rowTarget(row, feedId, "acknowledged");
  if (!intent) return null;
  if (intent.subject === "feed_item") return { targetKind: "feed_item", targetId: intent.feedItemId, feedId };
  if (intent.subject === "feed") return null;
  return { targetKind: "anchor", targetId: intent.anchorId, feedId };
}

/**
 * The reassignment prototype's writes: hand rows to a member, a team, or a Driver Manager book, or clear them.
 *
 * One row per targeted item, into this browser's store alone (`recordAssignments`). Nothing is acknowledged: the
 * handoff stays open until the item is.
 *
 * @param _grain - The view's acknowledgement grain, unread: at either grain a row hands off one item, never a channel.
 * @param scope - Who hands off, in which workspace.
 */
export function useReassignment(
  _grain: AcknowledgeGrain,
  scope: { workspaceId: string; membershipId: string | undefined },
): {
  reassignRows: (rows: readonly DispatchTableRow[], assignee: AssignmentTarget, note: string | null) => void;
  clearRows: (rows: readonly DispatchTableRow[]) => void;
} {
  const { workspaceId, membershipId } = scope;

  const write = useCallback(
    (rows: readonly DispatchTableRow[], assignee: AssignmentTarget | null, note: string | null) => {
      const intents = rows.flatMap((row) => {
        const target = assignmentTargetOf(row);
        return target ? [{ ...target, assignee, note }] : [];
      });
      recordAssignments({ workspaceId, actorMembershipId: membershipId ?? null }, intents);
    },
    [workspaceId, membershipId],
  );

  const reassignRows = useCallback(
    (rows: readonly DispatchTableRow[], assignee: AssignmentTarget, note: string | null) => write(rows, assignee, note),
    [write],
  );
  const clearRows = useCallback((rows: readonly DispatchTableRow[]) => write(rows, null, null), [write]);

  return { reassignRows, clearRows };
}
